import { useMemo } from 'react';
import { TabItem } from '../../components/components.interfaces';
import { PageName } from '../../../utils/pages-helpers';
import FinanceList from './subviews/financeList/financeList.view';
import FinancialGrants from './subviews/financialGrants/financialGrants.view';
import FinancialReports from './subviews/financialReports/financialReports.view';
import { FinancialGrantsProvider } from './contexts/financialGrants.context';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { hasReadPermission } from 'src/utils/rbacHelpers';

export const financeSubRouteIds = [
  PageName.financeList,
  PageName.scholarships,
  PageName.financialReports,
] as const;

export const useFinanceTabMenuItems = (
  translateText: TTranslateTextType
): TabItem[] => {
  const { rbac } = useRbacState();

  return useMemo(() => {
    const tabs: TabItem[] = [];

    if (hasReadPermission(rbac, 'finance', 'financeList')) {
      tabs.push({
        label: translateText('finance.tabs.allFinances'),
        onClickHandler: () => {},
        tabPanel: <FinanceList />,
      });
    }

    if (hasReadPermission(rbac, 'finance', 'scholarships')) {
      tabs.push({
        label: translateText('finance.tabs.scholarshipsAndGrants'),
        onClickHandler: () => {},
        tabPanel: (
          <FinancialGrantsProvider>
            <FinancialGrants />
          </FinancialGrantsProvider>
        ),
      });
    }

    if (hasReadPermission(rbac, 'finance', 'financialReports')) {
      tabs.push({
        label: translateText('finance.tabs.financialReports'),
        onClickHandler: () => {},
        tabPanel: <FinancialReports />,
      });
    }

    return tabs;
  }, [rbac, translateText]);
};

/** Returns route IDs for tabs the user has read permission for, in display order */
export const useFinanceAvailableRouteIds = (): string[] => {
  const { rbac } = useRbacState();
  return useMemo(() => {
    const routes: string[] = [];
    if (hasReadPermission(rbac, 'finance', 'financeList'))
      routes.push(PageName.financeList);
    if (hasReadPermission(rbac, 'finance', 'scholarships'))
      routes.push(PageName.scholarships);
    if (hasReadPermission(rbac, 'finance', 'financialReports'))
      routes.push(PageName.financialReports);
    return routes;
  }, [rbac]);
};

export const getFinanceSubRouteId = (index: number): string => {
  return financeSubRouteIds[index];
};

export const getFinanceTabIndex = (subRouteId: string): number => {
  return financeSubRouteIds.indexOf(
    subRouteId as (typeof financeSubRouteIds)[number]
  );
};
